/*eslint-disable max-lines */
import createAreaPoint from '@gaia/utils/gaia/createAreaPoint';
import getAreaPolygon from '@gaia/utils/gaia/getAreaPolygon';
import {areaFixedLayerIds} from '@gaia/utils/gaia/getSyncMapboxStyle/fixedLayerIds';
import isPolygonValid from '@gaia/utils/gaia/isPolygonValid';
import removeAreaPoint from '@gaia/utils/gaia/removeAreaPoint';
import updateAreaPoint from '@gaia/utils/gaia/updateAreaPoint';
import type {
    MapCursor,
    UseInteractionHandlersOptions,
} from '@gaia/utils/gaia/useInteractionHandlers';
import isEqual from 'lodash/isEqual';
import values from 'lodash/values';

export const editingArea = {
    getCursor: (interaction: EditingAreaInteractionState): MapCursor => {
        const polygon = getAreaPolygon(interaction.area);
        if (interaction.subtype === 'appendingPoint' && interaction.mode === 'create') {
            return 'crosshair';
        } else if (interaction.subtype === 'hoveringPoint') {
            return 'grab';
        } else if (
            (interaction.subtype === 'draggingPoint' || interaction.subtype === 'clickingPoint') &&
            isPolygonValid(polygon)
        ) {
            return 'not-allowed';
        } else if (interaction.subtype === 'draggingPoint') {
            return 'grabbing';
        } else if (interaction.subtype === 'clickingPoint') {
            return 'grabbing';
        } else if (interaction.subtype === 'hoveringLine') {
            return 'grab';
        } else {
            return 'default';
        }
    },

    onMouseDown: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingAreaInteractionState>
    ) => {
        if (interaction.subtype === 'hoveringPoint') {
            event.preventDefault();
            onInteractionChange({...interaction, subtype: 'clickingPoint'});
        } else if (interaction.subtype === 'hoveringLine') {
            event.preventDefault();
            onInteractionChange({
                ...interaction,
                subtype: 'clickingLine',
            });
        }
    },

    onMouseUp: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingAreaInteractionState>
    ) => {
        if (interaction.subtype === 'clickingPoint' || interaction.subtype === 'draggingPoint') {
            onInteractionChange({
                ...interaction,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });
        } else if (interaction.subtype === 'clickingLine') {
            event.preventDefault();

            const {area} = interaction;

            const updatedState = createAreaPoint({
                lngLat: event.lngLat,
                insertBeforePointLocalId: interaction.toPointLocalId,
                area,
            });

            if (!updatedState) return;
            const [pointLocalId, updatedArea] = updatedState;

            onInteractionChange({
                ...interaction,
                area: updatedArea,
                pointLocalId,
                subtype: 'clickingPoint',
            });
        }
    },

    onMouseMove: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
            setSelections,
        }: UseInteractionHandlersOptions<EditingAreaInteractionState>
    ) => {
        const map = event.target;

        if (interaction.subtype === 'clickingPoint' || interaction.subtype === 'draggingPoint') {
            // Drag the point to a new location
            onInteractionChange({...interaction, subtype: 'draggingPoint'});
            const {area, pointLocalId} = interaction;

            const updatedArea = updateAreaPoint({
                area,
                pointLocalId,
                lngLat: event.lngLat,
            });
            if (!updatedArea) return;
            onInteractionChange({
                ...interaction,
                area: updatedArea,
            });
        } else if (interaction.subtype === 'removingPoint') {
            // Remove the point from the area. Triggered via the AreaPointPopup
            const {area, pointLocalId} = interaction;
            const updatedArea = removeAreaPoint({area, pointLocalId});
            if (!updatedArea) return;
            onInteractionChange({
                ...interaction,
                area: updatedArea,
                subtype: 'appendingPoint',
                cursorLngLat: undefined,
            });
            setSelections([]);
        } else if (interaction.subtype === 'clickingLine') {
            // Create a new point at this position on the line
            const updatedState = createAreaPoint({
                lngLat: event.lngLat,
                insertBeforePointLocalId: interaction.toPointLocalId,
                area: interaction.area,
            });
            if (!updatedState) return;
            const [pointLocalId, area] = updatedState;

            onInteractionChange({
                ...interaction,
                area,
                pointLocalId,
                subtype: 'draggingPoint',
            });
        } else {
            // Check whether the cursor is over an existing feature
            const features = map.queryRenderedFeatures(
                [
                    [event.point.x - 2, event.point.y + 2],
                    [event.point.x + 2, event.point.y - 2],
                ],
                {
                    layers: [
                        areaFixedLayerIds.editingArea.point,
                        areaFixedLayerIds.editingArea.line,
                    ].filter((id) => map.getLayer(id)),
                    filter: [
                        'any',
                        ['==', ['get', 'localId'], interaction.area.properties.localId],
                        ['==', ['get', 'areaLocalId'], interaction.area.properties.localId],
                    ],
                }
            );

            const feature = features[0];

            if (!feature) {
                onInteractionChange({
                    ...interaction,
                    subtype: 'appendingPoint',
                    cursorLngLat: event.lngLat,
                });
            } else if (feature.geometry.type === 'Point') {
                onInteractionChange({
                    ...interaction,
                    pointLocalId: (feature as any).properties.localId,
                    subtype: 'hoveringPoint',
                    lngLat: event.lngLat,
                });
            } else if (feature.properties && feature.properties.type === 'normal') {
                onInteractionChange({
                    ...interaction,
                    subtype: 'hoveringLine',
                    toPointLocalId: (feature as any).properties.toPointLocalId,
                    lngLat: event.lngLat,
                });
            }
        }
    },

    // Cursor has moved outside of the map bounds or is hovering a non-map element
    onMouseOut: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingAreaInteractionState>
    ) => {
        if (interaction.subtype === 'appendingPoint') {
            onInteractionChange({...interaction, cursorLngLat: undefined});
        }
    },

    onClick: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
            setSelections,
            selections,
            sidebar,
            onSidebarChange,
        }: UseInteractionHandlersOptions<EditingAreaInteractionState>
    ) => {
        if (sidebar.type !== 'editArea') {
            // Reopen the sidebar if it was closed while editing. This supports the use case
            // where a user might need to change layers or other properties while editing an area.
            onSidebarChange({
                type: 'editArea',
                id: interaction.area.properties.localId,
            });
        } else if (interaction.subtype === 'appendingPoint' && interaction.mode === 'create') {
            // Create a new point at this location
            const updatedState = createAreaPoint({
                lngLat: event.lngLat,
                area: interaction.area,
            });
            if (!updatedState) return;

            const [pointLocalId, area] = updatedState;

            onInteractionChange({
                ...interaction,
                area,
                pointLocalId,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });
        } else if (interaction.subtype === 'hoveringPoint') {
            // Select the point that is being hovered
            const nextSelections: WaypointSelectionState[] = [
                {
                    type: 'waypoint',
                    localId: interaction.pointLocalId,
                    areaLocalId: interaction.area.properties.localId,
                },
            ];
            setSelections(isEqual(selections, nextSelections) ? [] : nextSelections);
        } else if (interaction.subtype === 'clickingPoint') {
            // Finish the click and return to hovering
            onInteractionChange({
                ...interaction,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });
        }
    },

    onDblClick: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            areaDispatcher,
            interaction,
            onSidebarChange,
            onInteractionChange,
            setSelections,
            isAuthenticated,
        }: UseInteractionHandlersOptions<EditingAreaInteractionState>
    ) => {
        if (interaction.subtype === 'hoveringPoint') {
            event.preventDefault();
            const {area} = interaction;
            if (!area.isGeometryLoaded || !area.isGeometryEditable) return;
            const point = area.points[interaction.pointLocalId];

            // Finish the polygon if the user double clicks on the first or last point
            if (
                (point.properties.order === 0 ||
                    point.properties.order === values(area.points).length - 1) &&
                interaction.mode === 'create' &&
                isAuthenticated
            ) {
                const polygon = getAreaPolygon(area);
                if (!isPolygonValid(polygon)) return;

                areaDispatcher({type: 'update', area});
                onSidebarChange({
                    type: 'areaDetails',
                    id: area.properties.localId,
                });
                onInteractionChange({type: 'normal', subtype: 'normal'});

                setSelections([
                    {
                        type: 'area',
                        localId: interaction.area.properties.localId,
                    },
                ]);

                onInteractionChange({type: 'normal', subtype: 'normal'});
            }
        }
    },
};
